import SfccAPI from 'sfccAPI';
import CommerceAPI from 'commerceAPI';
import ConfigurationsMgr from 'coreClasses/configurations/ConfigurationsMgr';
import ProductsMgr from 'coreClasses/product/ProductsMgr';
import SearchMgr from 'coreClasses/search/SearchMgr';
import CustomerMgr from 'coreClasses/customer/CustomerMgr';
import OrdersMgr from 'coreClasses/order/OrdersMgr';
import RecommendationsMgr from 'coreClasses/recommendations/RecommendationsMgr';
import GiftCertificatesMgr from 'coreClasses/giftCertificates/GiftCertificatesMgr';
import PromotionsMgr from 'coreClasses/promotion/PromotionsMgr';
import LocalesMgr from 'coreClasses/locales/LocalesMgr';
import LanguagesMgr from 'coreClasses/languages/LanguagesMgr';
import CountriesMgr from 'coreClasses/countries/CountriesMgr';
import ContentMgr from 'coreClasses/content/ContentMgr';
import CMSProvidersMgr from 'coreClasses/content/CMSProvidersMgr';
import SeoMgr from 'coreClasses/seo/SeoMgr';
import BanningMgr from 'coreClasses/banning/BanningMgr';
import EventsMgr from 'coreClasses/events/EventsMgr';
import NamespaceEventManager from 'coreClasses/events/NamespaceEventManager';
import AnalyticsMgr from 'coreClasses/analytics/AnalyticsMgr';
import GeolocationMgr from 'coreClasses/geolocation/GeolocationMgr';
import SpinnerMgr from 'coreClasses/spinner/SpinnerMgr';
import ReturnAuthorizationMgr from 'coreClasses/returnAuthorization/ReturnAuthorizationMgr';
import UiMgr from 'coreClasses/ui/UiMgr';
import PopupsMgr from 'coreClasses/popups/PopupsMgr';
import ContentSecurityPolicyMgr from 'coreClasses/security/ContentSecurityPolicyMgr';
import IdentityProvidersMgr from 'coreClasses/identity/IdentityProvidersMgr';
import ErrorsStreamMgr from 'coreClasses/errorsStream/ErrorsStreamMgr';
import FlyoutsMgr from 'coreClasses/flyouts/FlyoutsMgr';
import LogMgr from 'coreClasses/log/LogMgr';
import LockMgr from 'coreClasses/lock/LockMgr';
import StringTemplateVariablesMgr from 'coreClasses/stringTemplateVariables/StringTemplateVariablesMgr';
import StringTemplateResolver from 'coreClasses/stringTemplateResolver/StringTemplateResolver';
import RouterMgr from 'coreClasses/router/RouterMgr';
import NewsletterMgr from 'coreClasses/newsletter/NewsletterMgr';
import EnvPreferencesMgr from 'coreClasses/environment/EnvPreferencesMgr';
import StaticCacheMgr from 'coreClasses/static-cache/StaticCacheMgr';
import {TStaticCache} from 'coreClasses/static-cache/IStaticCache';
import StoreMgr from 'brand/app/store/StoreMgr';

/**
 * Represents ManagerRepository
 */
abstract class ManagerRepository {
	private readonly commerceApi: CommerceAPI;
	private readonly sfccAPI: SfccAPI;
	private readonly configurationsMgr: ConfigurationsMgr;
	private readonly productsMgr: ProductsMgr;
	private readonly customerMgr: CustomerMgr;
	private readonly ordersMgr: OrdersMgr;
	private readonly recommendationsMgr: RecommendationsMgr;
	private readonly giftCertificatesMgr: GiftCertificatesMgr;
	private readonly promotionsMgr: PromotionsMgr;
	private readonly localesMgr: LocalesMgr;
	private readonly envPreferencesMgr: EnvPreferencesMgr;
	private readonly languagesMgr: LanguagesMgr;
	private readonly countriesMgr: CountriesMgr;
	private readonly searchMgr: SearchMgr;
	private readonly cmsProvidersMgr: CMSProvidersMgr;
	private readonly contentMgr: ContentMgr;
	private readonly storeMgr: StoreMgr;
	private readonly seoMgr: SeoMgr;
	private readonly banningMgr: BanningMgr;
	private readonly eventsMgr: EventsMgr;
	private readonly analyticsMgr: AnalyticsMgr;
	private readonly geolocationMgr: GeolocationMgr;
	private readonly spinnerMgr: SpinnerMgr;
	private readonly returnAuthorizationMgr: ReturnAuthorizationMgr;
	protected readonly uiMgr: UiMgr;
	private readonly popupsMgr: PopupsMgr;
	private readonly identityProvidersMgr: IdentityProvidersMgr;
	private readonly flyoutsMgr: FlyoutsMgr;
	private readonly errorsStreamMgr: ErrorsStreamMgr;
	private readonly contentSecurityPolicyMgr: ContentSecurityPolicyMgr;
	private readonly logMgr: LogMgr;
	private readonly lockMgr: LockMgr;
	private readonly stringTemplateVariablesMgr: StringTemplateVariablesMgr;
	private readonly stringTemplateResolver: StringTemplateResolver;
	private readonly routerManager: RouterMgr;
	protected readonly newsletterMgr: NewsletterMgr;
	private readonly staticCacheMgr: StaticCacheMgr;

	/**
	 * @constructor
	 * @param {Object | null} initialState - initial redux state
	 * @param {Object} apiConfig - api config
	 * @param {Object} staticCache - static cache object
	 * @param {Object} envPreferences - env preferences object
	 * @protected
	 */
	protected constructor(initialState: object | null, apiConfig: object, staticCache: object, envPreferences: object) {
		this.storeMgr = new StoreMgr(this, initialState);
		this.errorsStreamMgr = new ErrorsStreamMgr(this);
		this.configurationsMgr = new ConfigurationsMgr(this);
		this.countriesMgr = new CountriesMgr(this);
		this.languagesMgr = new LanguagesMgr(this);
		this.localesMgr = new LocalesMgr(this);
		this.envPreferencesMgr = new EnvPreferencesMgr(this, envPreferences);

		const currentLocale = this.localesMgr.getCurrentLocale();
		const defaultLocale = this.localesMgr.getDefaultLocale();
		const currencyCode = currentLocale.getCurrencyCode() || defaultLocale.getCurrencyCode();
		const languageCode = currentLocale.getLanguageCode() || defaultLocale.getLanguageCode();
		const countryCode = currentLocale.getCountryCode() || defaultLocale.getCountryCode();

		this.uiMgr = new UiMgr(this);
		this.sfccAPI = new SfccAPI(this);
		this.commerceApi = new CommerceAPI({...apiConfig, locale: languageCode, currency: currencyCode} as any, this);
		this.sfccAPI.setLocale(languageCode, countryCode);
		this.productsMgr = new ProductsMgr(this);
		this.customerMgr = new CustomerMgr(this);
		this.ordersMgr = new OrdersMgr(this);
		this.recommendationsMgr = new RecommendationsMgr(this);
		this.giftCertificatesMgr = new GiftCertificatesMgr(this);
		this.promotionsMgr = new PromotionsMgr(this);
		this.searchMgr = new SearchMgr(this);
		this.contentMgr = new ContentMgr(this);
		this.cmsProvidersMgr = new CMSProvidersMgr(this);
		this.seoMgr = new SeoMgr(this);
		this.banningMgr = new BanningMgr(this);
		this.eventsMgr = new EventsMgr(this);
		this.analyticsMgr = new AnalyticsMgr(this);
		this.spinnerMgr = new SpinnerMgr(this);
		this.returnAuthorizationMgr = new ReturnAuthorizationMgr(this);
		this.popupsMgr = new PopupsMgr(this);
		this.flyoutsMgr = new FlyoutsMgr(this);
		this.analyticsMgr.initialize();
		this.geolocationMgr = new GeolocationMgr(this);
		this.contentSecurityPolicyMgr = new ContentSecurityPolicyMgr(this);
		this.identityProvidersMgr = new IdentityProvidersMgr(this);
		this.logMgr = new LogMgr(this);
		this.lockMgr = new LockMgr(this);
		this.eventsMgr.subscribe(this.recommendationsMgr.getProvider());
		this.eventsMgr.subscribe(this.analyticsMgr);
		this.stringTemplateVariablesMgr = new StringTemplateVariablesMgr(this);
		this.stringTemplateResolver = new StringTemplateResolver(this.stringTemplateVariablesMgr);
		this.routerManager = new RouterMgr(this);
		this.newsletterMgr = new NewsletterMgr(this);
		this.staticCacheMgr = new StaticCacheMgr(this, staticCache as TStaticCache);
	}

	/**
	 * Returns the ConfigurationsMgr instance
	 * @returns ConfigurationsMgr
	 */
	public getConfigurationsMgr(): ConfigurationsMgr {
		return this.configurationsMgr;
	}

	/**
	 * Returns the ProductsMgr instance
	 * @returns ProductsMgr
	 */
	public getProductsMgr(): ProductsMgr {
		return this.productsMgr;
	}

	/**
	 * Returns the CustomerMgr instance
	 * @returns CustomerMgr
	 */
	public getCustomerMgr(): CustomerMgr {
		return this.customerMgr;
	}

	/**
	 * Returns the OrdersMgr instance
	 * @returns OrdersMgr
	 */
	public getOrdersMgr(): OrdersMgr {
		return this.ordersMgr;
	}

	/**
	 * Returns the RecommendationsMgr instance
	 * @returns RecommendationsMgr
	 */
	public getRecommendationsMgr(): RecommendationsMgr {
		return this.recommendationsMgr;
	}

	/**
	 * Returns the GiftCertificatesMgr instance
	 * @returns GiftCertificatesMgr
	 */
	public getGiftCertificatesMgr(): GiftCertificatesMgr {
		return this.giftCertificatesMgr;
	}

	/**
	 * Returns the PromotionsMgr instance
	 * @returns PromotionsMgr
	 */
	public getPromotionsMgr(): PromotionsMgr {
		return this.promotionsMgr;
	}

	/**
	 * Returns the LocalesMgr instance
	 * @returns LocalesMgr
	 */
	public getLocalesMgr(): LocalesMgr {
		return this.localesMgr;
	}

	/**
	 * Returns the EnvPreferencesMgr instance
	 * @returns EnvPreferencesMgr
	 */
	public getEnvPreferencesMgr(): EnvPreferencesMgr {
		return this.envPreferencesMgr;
	}

	/**
	 * Returns the LanguagesMgr instance
	 * @returns LanguagesMgr
	 */
	public getLanguagesMgr(): LanguagesMgr {
		return this.languagesMgr;
	}

	/**
	 * Returns the CountriesMgr instance
	 * @returns CountriesMgr
	 */
	public getCountriesMgr(): CountriesMgr {
		return this.countriesMgr;
	}

	/**
	 * Returns the SearchMgr instance
	 * @returns SearchMgr
	 */
	public getSearchMgr(): SearchMgr {
		return this.searchMgr;
	}

	/**
	 * Returns the ContentMgr instance
	 * @returns ContentMgr
	 */
	public getContentMgr(): ContentMgr {
		return this.contentMgr;
	}

	/**
	 * Returns the CMSProvidersMgr instance
	 * @returns CMSProvidersMgr
	 */
	public getCmsProvidersMgr(): CMSProvidersMgr {
		return this.cmsProvidersMgr;
	}

	/**
	 * Returns the StoreMgr instance
	 * @returns StoreMgr
	 */
	public getStoreMgr(): StoreMgr {
		return this.storeMgr;
	}

	/**
	 * Returns the SeoMgr instance
	 * @returns SeoMgr
	 */
	public getSeoMgr(): SeoMgr {
		return this.seoMgr;
	}

	/**
	 * Returns the BanningMgr instance
	 * @returns BanningMgr
	 */
	public getBanningMgr(): BanningMgr {
		return this.banningMgr;
	}

	/**
	 * Returns the EventsMgr instance
	 * @returns EventsMgr
	 */
	public getEventsMgr(): EventsMgr {
		return this.eventsMgr;
	}

	/**
	 * Returns the NamespaceEventManager instance
	 * @returns NamespaceEventManager
	 */
	public getNamespaceEventManager(namespace: string): NamespaceEventManager {
		return this.getEventsMgr().getNamespace(namespace);
	}

	/**
	 * Returns the AnalyticsMgr instance
	 * @returns AnalyticsMgr
	 */
	public getAnalyticsMgr(): AnalyticsMgr {
		return this.analyticsMgr;
	}

	/**
	 * Returns the GeolocationMgr instance
	 * @returns GeolocationMgr
	 */
	public getGeolocationMgr(): GeolocationMgr {
		return this.geolocationMgr;
	}

	/**
	 * Returns the IdentityProvidersMgr instance
	 * @returns IdentityProvidersMgr
	 */
	public getIdentityProvidersMgr(): IdentityProvidersMgr {
		return this.identityProvidersMgr;
	}

	/**
	 * Returns the ContentSecurityPolicyMgr instance
	 * @returns ContentSecurityPolicyMgr
	 */
	public getContentSecurityPolicyMgr(): ContentSecurityPolicyMgr {
		return this.contentSecurityPolicyMgr;
	}

	/**
	 * Returns the SpinnerMgr instance
	 * @returns SpinnerMgr
	 */
	public getSpinnerMgr(): SpinnerMgr {
		return this.spinnerMgr;
	}

	/**
	 * Returns the ReturnAuthorizationMgr instance
	 * @returns ReturnAuthorizationMgr
	 */
	public getReturnAuthorizationMgr(): ReturnAuthorizationMgr {
		return this.returnAuthorizationMgr;
	}

	/**
	 * Returns the UiMgr instance
	 * @returns UiMgr
	 */
	public getUiMgr(): UiMgr {
		return this.uiMgr;
	}

	/**
	 * Returns the PopupsMgr instance
	 * @returns PopupsMgr
	 */
	public getPopupsMgr(): PopupsMgr {
		return this.popupsMgr;
	}

	/**
	 * Returns the FlyoutsMgr instance
	 * @returns FlyoutsMgr
	 */
	public getFlyoutsMgr(): FlyoutsMgr {
		return this.flyoutsMgr;
	}

	/**
	 * Returns the ErrorsStreamMgr instance
	 * @returns ErrorsStreamMgr
	 */
	public getErrorsStreamMgr(): ErrorsStreamMgr {
		return this.errorsStreamMgr;
	}

	/**
	 * Returns the LogMgr instance
	 * @returns LogMgr
	 */
	public getLogMgr(): LogMgr {
		return this.logMgr;
	}

	/**
	 * Returns the LockMgr instance
	 * @returns LockMgr
	 */
	public getLockMgr(): LockMgr {
		return this.lockMgr;
	}

	/**
	 * Returns the Commerce API instance
	 * @returns CommerceAPI
	 */
	public getCommerceApi(): CommerceAPI {
		return this.commerceApi;
	}

	/**
	 * Returns the SFCC API instance
	 * @returns SfccAPI
	 */
	public getSfccAPI(): SfccAPI {
		return this.sfccAPI;
	}

	/**
	 * Returns the StringTemplateVariablesMgr instance
	 * @returns StringTemplateVariablesMgr
	 */
	public getStringTemplateVariablesMgr(): StringTemplateVariablesMgr {
		return this.stringTemplateVariablesMgr;
	}

	/**
	 * Returns the StringTemplateResolver instance
	 * @returns StringTemplateResolver
	 */
	public getStringTemplateResolver(): StringTemplateResolver {
		return this.stringTemplateResolver;
	}

	/**
	 * Returns the RouterMgr instance
	 * @returns LockMgr
	 */
	public getRouterMgr(): RouterMgr {
		return this.routerManager;
	}

	/**
	 * Returns the NewsletterMgr instance
	 * @returns NewsletterMgr
	 */
	public getNewsletterMgr(): NewsletterMgr {
		return this.newsletterMgr;
	}

	/**
	 * Returns the StaticCacheMgr instance
	 * @returns StaticCacheMgr
	 */
	public getStaticCacheMgr(): StaticCacheMgr {
		return this.staticCacheMgr;
	}
}

export default ManagerRepository;
